// ============================================================================
// 📦 VERSION: 2025.12.04.0001
// ============================================================================
// CHANGELOG:
// 2025.12.04.0001 - Initial creation - Reward Claim Modal with animations
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { getUnclaimedRewards, claimReward, claimAllRewards } from '../rewardsApi';

const RewardClaimModal = ({ 
  isOpen, 
  onClose, 
  onClaim,  // Callback when rewards are claimed (to refresh stats)
  isAuthenticated = false 
}) => {
  const [rewards, setRewards] = useState([]);
  const [totals, setTotals] = useState({ count: 0, traks: 0, tags: 0 });
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimedRewards, setClaimedRewards] = useState(new Set());
  const [showConfetti, setShowConfetti] = useState(false);
  const [claimResult, setClaimResult] = useState(null);

  // Reward type styling
  const rewardTypeConfig = {
    achievement: { icon: '🏆', color: 'from-yellow-500 to-orange-500', label: 'Achievement' },
    challenge_approved: { icon: '🎁', color: 'from-blue-500 to-cyan-500', label: 'Challenge Approved' },
    referral: { icon: '👋', color: 'from-pink-500 to-rose-500', label: 'Referral Bonus' },
    admin_award: { icon: '⭐', color: 'from-amber-500 to-yellow-500', label: 'Admin Award' }
  };

  // Load unclaimed rewards
  const loadRewards = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getUnclaimedRewards();
      if (data?.success) {
        setRewards(data.unclaimed || []);
        setTotals(data.totals || { count: 0, traks: 0, tags: 0 });
      }
    } catch (error) {
      console.error('Failed to load unclaimed rewards:', error);
    }
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isOpen) {
      loadRewards();
      setClaimedRewards(new Set());
      setClaimResult(null);
    }
  }, [isOpen, loadRewards]);

  // Claim single reward
  const handleClaimOne = async (rewardId) => {
    setClaiming(true);
    const result = await claimReward(rewardId);
    
    if (result?.success) {
      setClaimedRewards(prev => new Set([...prev, rewardId]));
      
      // Update totals
      const reward = rewards.find(r => r.id === rewardId);
      if (reward) {
        setTotals(prev => ({
          count: prev.count - 1,
          traks: prev.traks - (reward.traks_earned || 0),
          tags: prev.tags - (reward.tags_earned || 0)
        }));
      }
      
      // Trigger confetti for big rewards
      if ((reward?.traks_earned || 0) >= 5 || reward?.reward_type === 'achievement') {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2000);
      }
      
      // Callback to parent
      if (onClaim) onClaim(result.newTotals);
    }
    setClaiming(false);
  };

  // Claim all rewards
  const handleClaimAll = async () => {
    setClaiming(true);
    const result = await claimAllRewards();
    
    if (result?.success) {
      setClaimResult(result.claimed);
      setShowConfetti(true);
      
      // Mark all as claimed
      setClaimedRewards(new Set(rewards.map(r => r.id)));
      setTotals({ count: 0, traks: 0, tags: 0 });
      
      // Callback to parent
      if (onClaim) onClaim(result.newTotals);
      
      // Close after animation
      setTimeout(() => {
        setShowConfetti(false);
        onClose();
      }, 3000);
    }
    setClaiming(false);
  };

  // Format relative time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMins = Math.floor((now - date) / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  if (!isOpen) return null;

  // Filter out already claimed rewards
  const unclaimedRewards = rewards.filter(r => !claimedRewards.has(r.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Confetti Effect */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-20px',
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            >
              {['🎉', '✨', '⭐', '🏆', '💰'][Math.floor(Math.random() * 5)]}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <div className="relative bg-gray-900 rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden shadow-2xl border border-gray-700 animate-modalSlideIn">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 p-5 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.2),transparent)]" />
          
          <div className="relative">
            <div className="text-5xl mb-2 animate-bounce">🎁</div>
            <h2 className="text-white font-bold text-xl">Rewards Waiting!</h2>
            
            {totals.count > 0 && (
              <div className="flex justify-center gap-4 mt-3 text-sm">
                <span className="bg-white/20 px-3 py-1 rounded-full text-white">
                  <span className="font-bold">{totals.count}</span> {totals.count === 1 ? 'reward' : 'rewards'}
                </span>
                {totals.traks > 0 && (
                  <span className="bg-yellow-500/30 px-3 py-1 rounded-full text-yellow-200">
                    🏆 <span className="font-bold">+{totals.traks}</span> TRAKs
                  </span>
                )}
                {totals.tags > 0 && (
                  <span className="bg-green-500/30 px-3 py-1 rounded-full text-green-200">
                    🎯 <span className="font-bold">+{totals.tags}</span> tags
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
        >
          ✕
        </button>

        {/* Content */}
        <div className="p-4 max-h-[45vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin text-4xl mb-2">⏳</div>
              <p className="text-gray-400">Loading rewards...</p>
            </div>
          ) : claimResult ? (
            // Claim success state
            <div className="text-center py-8">
              <div className="text-6xl mb-4 animate-pulse">🎉</div>
              <h3 className="text-white text-xl font-bold mb-2">All Claimed!</h3>
              <div className="flex justify-center gap-4 text-lg">
                {claimResult.traks > 0 && (
                  <span className="text-yellow-400 font-bold">+{claimResult.traks} TRAKs</span>
                )}
                {claimResult.tags > 0 && (
                  <span className="text-green-400 font-bold">+{claimResult.tags} tags</span>
                )}
              </div>
            </div>
          ) : unclaimedRewards.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-5xl mb-3">📭</div>
              <p className="text-gray-400">No rewards to claim right now.</p>
              <p className="text-gray-500 text-sm mt-1">Keep playing to earn more!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {unclaimedRewards.map((reward, index) => {
                const config = rewardTypeConfig[reward.reward_type] || {
                  icon: '🎁',
                  color: 'from-gray-500 to-gray-600',
                  label: 'Reward'
                };
                
                return (
                  <div
                    key={reward.id}
                    className="bg-gray-800 rounded-xl p-4 flex items-center gap-3 animate-fadeInUp border border-gray-700 hover:border-gray-600 transition-colors"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${config.color} flex items-center justify-center text-2xl flex-shrink-0 shadow-lg`}>
                      {config.icon}
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">
                        {reward.description}
                      </p>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {config.label} • {formatTime(reward.created_at)}
                      </p>
                      <div className="flex gap-2 mt-1">
                        {reward.traks_earned > 0 && (
                          <span className="text-yellow-400 text-xs font-bold">+{reward.traks_earned} TRAKs</span>
                        )}
                        {reward.tags_earned > 0 && (
                          <span className="text-green-400 text-xs font-bold">+{reward.tags_earned} tags</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Claim Button */}
                    <button
                      onClick={() => handleClaimOne(reward.id)}
                      disabled={claiming}
                      className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-bold rounded-lg text-sm shadow-lg hover:shadow-green-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      {claiming ? '...' : 'Claim'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Claim All Button */}
        {unclaimedRewards.length > 0 && !claimResult && (
          <div className="p-4 border-t border-gray-700 bg-gray-800/50">
            <button
              onClick={handleClaimAll}
              disabled={claiming}
              className="w-full py-4 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 hover:from-purple-500 hover:via-pink-400 hover:to-orange-400 text-white font-bold rounded-xl text-lg shadow-xl hover:shadow-purple-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {claiming ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Claiming...
                </>
              ) : (
                <>
                  <span className="text-xl">✨</span>
                  Claim All Rewards
                  <span className="text-xl">✨</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        .animate-modalSlideIn {
          animation: modalSlideIn 0.3s ease-out;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.4s ease-out both;
        }
        
        .animate-confetti {
          animation: confetti 3s ease-out forwards;
          font-size: 24px;
        }
      `}</style>
    </div>
  );
};

export default RewardClaimModal;
